<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useTenantStore, useAuthStore } from '@/store';
import { TenantInterface, TenantFilterInterface } from '@/types';
import { useRouting, useDateTime } from '@/composables';
import { DeleteTenantConfirm, RestoreTenantConfirm, AddTenantWizard } from '../';

const tenantStore = useTenantStore();
const authStore = useAuthStore();
const $routing = useRouting();
const showAddTenantWizard = ref(false);
const impersonateLoading = ref(false);

/**
 * Define the available default filters.
 */
const defaultFilters: TenantFilterInterface = {
  archived: false,
  search: '',
  page: '',
  per_page: 15,
};

interface menutItemInterface {
  label: string;
  command: () => Promise<void> | void;
}

const openDeleteConfirm = ref(false);
const openRestoreConfirm = ref(false);
const deleteId = ref();
const restoreId = ref();

/**
 * Define the filters with the default set.
 */
const filters = ref<TenantFilterInterface>({});

onMounted(() => {
  init();
});

const init = async () => {
  checkQuickbooksAuthenticationQueryParam();

  await defineFilters();
  await tenantStore.getTenants($routing.route.query);
};

const checkQuickbooksAuthenticationQueryParam = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  if (urlParams.get('quickbooks_error')) {
    const errorStr = urlParams.get('quickbooks_error');
    const error = errorStr === 'true';
    setTimeout(() => {
      tenantStore.doToast('Quickbooks authenticated', error);
    }, 1000);
  }
};

/**
 * Check whether there are any filters defined by check for query
 * params within the route object.
 */
const hasFilter = computed<boolean>(() => !!Object.keys($routing.route.query).length);

/**
 * TODO: Watch for changes to the route object in order to execute a
 * new request for data in filter etc
 */
$routing.routeChangeWatcher(async () => {
  await tenantStore.getTenants($routing.route.query);
});

/**
 * Handle the pagination of the request data.
 */
const onPage = (event) => {
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });
};

/**
 * Handle the ordering of the request data.
 */
const onSort = (event) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    archived: query.archived ? true : false,
    search: query.search ? (query.search as string) : '',
    page: query.page ? (query.page as string) : '',
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
  };
};

/**
 * Handle the clearing and resetting of the filters and route
 * query params.
 */
const onClearFilters = () => {
  filters.value = { ...defaultFilters };

  $routing.removeAllQueryParam();
};

/**
 * create the Action Menu model for the dataTable
 */
const createMenuModel = (item: TenantInterface) => {
  const menuItems: menutItemInterface[] = [{ label: 'Access Tenant', command: () => accessTenant(item) }];
  //if the tenant is eLuma do NOT allow deactivation
  if (item.master) return menuItems;

  const del = {
    label: 'Deactivate',
    command: () => {
      openConfirmDeleteTenant(item.id);
    },
  };

  const restore = {
    label: 'Restore',
    command: () => {
      openConfirmRestoreTenant(item.id);
    },
  };
  item.deleted_at ? menuItems.push(restore) : menuItems.push(del);
  return menuItems;
};

const accessTenant = async (tenant: TenantInterface) => {
  authStore.loading = true;
  const currentTenant = authStore.token?.tenant || undefined;
  let tenantToSwitch = authStore.token;

  if (currentTenant?.id !== tenant.id) {
    tenantToSwitch = await tenantStore.getTokenToSwitchTenant(tenant.id);
  }

  if (tenantToSwitch) await authStore.setTenantToSwitch(tenantToSwitch);
};

const openConfirmDeleteTenant = (id: number) => {
  deleteId.value = id;
  openDeleteConfirm.value = true;
};

const closeConfirmDeleteTenant = () => {
  openDeleteConfirm.value = false;
  deleteId.value = null;
};

const openConfirmRestoreTenant = (id: number) => {
  restoreId.value = id;
  openRestoreConfirm.value = true;
};

const closeConfirmRestoreTenant = () => {
  openRestoreConfirm.value = false;
  restoreId.value = null;
};

const startAddTenantWizard = () => {
  tenantStore.resetTenantStore();
  showAddTenantWizardModal();
};
const showAddTenantWizardModal = () => (showAddTenantWizard.value = true);

const closeAddTenantWizardModal = () => (showAddTenantWizard.value = false);

/**
 * Handle the filtering of the archived query.
 */
const onArchived = () => {
  if (filters.value.archived) {
    $routing.addQueryParam('archived', 'true');
  } else {
    $routing.removeQueryParam('archived');
  }
};

/**
 * Handle the filtering of results based on the provided search terms,
 * given 3 or or more characters have been provided.
 */
const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('search');
  }
};
</script>

<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="pb-2">Tenants</h1>
    </div>
    <div class="page-content">
      <DeleteTenantConfirm :id="deleteId" :open="openDeleteConfirm" @update="init" @cancel="closeConfirmDeleteTenant" />
      <RestoreTenantConfirm
        :id="restoreId"
        :open="openRestoreConfirm"
        @update="init"
        @cancel="closeConfirmRestoreTenant"
      />
      <AddTenantWizard v-if="showAddTenantWizard" @cancel="closeAddTenantWizardModal" @save="init" />
      <Card>
        <template #content>
          <div class="flex flex-wrap gap-3 py-3">
            <div class="flex flex-1 gap-3">
              <IconField icon-position="left">
                <InputIcon>
                  <Icon name="search" class="text-lg" />
                </InputIcon>
                <InputText v-model="filters.search" v-debounce:600ms="onSearch" placeholder="Search by Name" />
              </IconField>
              <div class="flex align-items-center">
                <div class="mr-2">
                  <InputSwitch v-model="filters.archived" @input="onArchived" />
                </div>
                <div class="text-color-secondary">
                  <label>Show Archived</label>
                </div>
              </div>
              <AppButton
                v-if="hasFilter"
                type="secondary"
                text="Clear Filters"
                icon-left="clear"
                @click="onClearFilters"
              />
            </div>
            <AppButton
              type="primary"
              class="admin-button"
              text="Add Tenant"
              icon-left="add"
              @click="startAddTenantWizard"
            />
          </div>

          <DataTable
            v-model:filters="filters"
            filter-display="menu"
            :value="tenantStore.tenants"
            :total-records="tenantStore.tenants.length"
            data-key="id"
            :rows="filters.per_page"
            :lazy="true"
            :paginator="true"
            loading-icon="logo-spinner"
            :loading="tenantStore.loading"
            :rows-per-page-options="[15, 25, 30]"
            paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            current-page-report-template="Showing {first} to {last} of {totalRecords}"
            responsive-layout="scroll"
            @page="onPage"
            @sort="onSort"
          >
            <template #empty>No tenants found</template>
            <template #loading>Loading. Please wait.</template>
            <Column field="name" header="Name">
              <template #body="{ data }">
                <RouterLink :to="`/admin/tenant/${data.id}`" class="font-bold" v-text="data.name" />
              </template>
            </Column>
            <Column field="city" header="City">
              <template #body="{ data }">
                {{ data.location ? data.location.city.name : '' }}
              </template>
            </Column>
            <Column field="state" header="State">
              <template #body="{ data }">
                {{ data.location ? data.location.state.name : '' }}
              </template>
            </Column>
            <Column field="created_at" header="Date Created">
              <template #body="{ data }">
                <span v-text="useDateTime().formatIntlDate(data.created_at)" />
              </template>
            </Column>
            <Column field="actions" header="Actions" header-style="width: 4em" class="text-center">
              <template #body="{ data }">
                <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
              </template>
            </Column>
          </DataTable>
        </template>
      </Card>
    </div>
  </div>
  <Loader v-if="impersonateLoading" :full-page="true" />
</template>

<style lang="scss" scoped>
:deep(.p-datatable-wrapper) {
  margin: 0px !important;
  max-height: 744px;
  overflow-y: auto;
}

:deep(.p-paginator-current) {
  flex: 1;
  justify-content: flex-end;
}
</style>
